import {Poi} from '../../react-app-env';
import './PoiDetailsSidebarPage.scss';
import ListView from '../listview/ListView';
import metaDataService, {PoiMetaData} from '../../services/MetaDataService';
import React, {useState} from 'react';
import GeneralGroup from './GeneralGroup';
import LocationGroup from './LocationGroup';
import LinksGroup from './LinksGroup';
import ScheduleGroup from './ScheduleGroup';
import ActionsGroup from './ActionsGroup';
import Icon, {IconSizes} from '../icon/Icon';
import {i18n} from '../../i18n/i18n';
import DescriptionGroup from './DescriptionGroup';
import LoadingState from './LoadingState';
import {StackApi} from '../stack/Stack.types';
import SidebarHeader, {buildPopStackButton} from '../sidebar/SidebarHeader';
import {SidebarPage} from '../sidebar/SidebarPage';
import RouteEffectSidebarPage from './routeeffect/RouteEffectSidebarPage';
import PoiGroupSidebarPage from './poigroup/PoiGroupSidebarPage';
import PoiCategorySidebarPage from './poicategory/PoiCategorySidebarPage';
import SidebarScrollableContent from '../sidebar/SidebarScrollableContent';
import {Events, EventType} from '../../events/Events';
import {useReadonly} from '../context/ReadonlyContext';
import {ifFalse} from '../../utils/Utils';

export interface PoiDetailsSidebarPageProps {
  readonly poi: Poi;
  readonly stackApi: StackApi;
}

function PoiDetailsSidebarPage({ poi, stackApi }: PoiDetailsSidebarPageProps) {
  const readonly = useReadonly();
  const ifWriteAccess = ifFalse(readonly);

  const [loading, setLoading] = useState(true);
  const [metaData, setMetaData] = useState<PoiMetaData | null>(null);
  const [metaDataError, setMetaDataError] = useState(false);

  metaDataService
      .loadMetaData(poi)
      .then(metaData => setMetaData(metaData))
      .catch(() => setMetaDataError(true))
      .finally(() => setLoading(false));

  function onPoiCategoryClicked() {
    stackApi.push({
      id:   'poi-category--' + poi.id,
      node: <PoiCategorySidebarPage
                poi={poi}
                stackApi={stackApi}/>
    });
  }

  function onPoiGroupClicked() {
    stackApi.push({
      id:   'poi-group--' + poi.id,
      node: <PoiGroupSidebarPage
                poi={poi}
                stackApi={stackApi}/>
    });
  }

  function onRouteEffectClicked() {
    stackApi.push({
      id:   'route-effect--' + poi.id,
      node: <RouteEffectSidebarPage
                poi={poi}
                stackApi={stackApi}/>
    });
  }

  if (stackApi == null) {
    return null;
  }

  return <SidebarPage className="poi-details">
    <SidebarHeader
        title={poi.title}
        buttons={[
          buildPopStackButton(() => {
            Events.publish(EventType.ON_POI_UNSELECTED);
          })
        ]}/>
    <SidebarScrollableContent>
      {metaDataError && <div className="poi-details--error">
        <Icon
          category="fa-circle-exclamation"
          size={IconSizes.SMALL}/>
        {i18n().poiDetails.metaDataErrorMessage}
      </div>}
      {loading && <LoadingState poi={poi}/>}
      {loading || <ListView>
        <GeneralGroup
          poi={poi}
          metaData={metaData}
          onPoiCategoryClicked={() => onPoiCategoryClicked()}
          onPoiGroupClicked={() => onPoiGroupClicked()}/>
        <DescriptionGroup
          poi={poi}
          metaData={metaData}/>
        <LocationGroup
          poi={poi}
          metaData={metaData}
          onRouteEffectClicked={() => onRouteEffectClicked()}/>
        <LinksGroup
          poi={poi}
          metaData={metaData}/>
        <ScheduleGroup poi={poi}
                       metaData={metaData}
                       stackApi={stackApi}/>
        {ifWriteAccess(
            <ActionsGroup poi={poi}/>
        )}
      </ListView>}
    </SidebarScrollableContent>
  </SidebarPage>;
}

export default PoiDetailsSidebarPage;
